import { memo, type ReactNode } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HomeIcon from "@mui/icons-material/Home";
import MedicationIcon from "@mui/icons-material/Medication";
import type { DocumentReference } from "fhir/r5";
import type { DocumentCategory } from "../../types";
import type { CategoryStatus } from "../../helpers/documentsChecklistHelpers";
import { DocumentTile, type RejectTarget } from "./DocumentTile";

const CATEGORY_ICONS: Record<string, ReactNode> = {
  anvisa_import_authorization: <FactCheckIcon fontSize="small" />,
  medical_prescription: <MedicationIcon fontSize="small" />,
  personal_document: <BadgeIcon fontSize="small" />,
  proof_of_residence: <HomeIcon fontSize="small" />,
};

const CATEGORY_STATUS_META: Record<
  CategoryStatus,
  { label: string; color: "default" | "info" | "success" | "error" }
> = {
  empty: { label: "Não enviado", color: "default" },
  pending: { label: "Aguardando", color: "info" },
  approved: { label: "Verificado", color: "success" },
  rejected: { label: "Recusado", color: "error" },
  mixed: { label: "Parcial", color: "info" },
};

interface CategoryCardProps {
  category: DocumentCategory;
  docs: DocumentReference[];
  status: CategoryStatus;
  organizationId?: string;
  canVerify: boolean;
  busyDocId: string | null;
  approvingAll: boolean;
  inheritedDocIds?: string[];
  onView: (doc: DocumentReference, title: string) => void;
  onApprove: (doc: DocumentReference, title: string) => void;
  onReject: (target: RejectTarget) => void;
  onRevokeApproval?: (doc: DocumentReference, title: string) => void;
  onRevokeRejection?: (doc: DocumentReference, title: string) => void;
}

export const CategoryCard = memo(function CategoryCard({
  category,
  docs,
  status,
  organizationId,
  canVerify,
  busyDocId,
  approvingAll,
  inheritedDocIds = [],
  onView,
  onApprove,
  onReject,
  onRevokeApproval,
  onRevokeRejection,
}: CategoryCardProps) {
  const statusMeta = CATEGORY_STATUS_META[status];
  const icon = CATEGORY_ICONS[category.code] ?? (
    <DescriptionIcon fontSize="small" />
  );
  const isEmpty = docs.length === 0;
  const isComplete = status === "approved";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: isComplete ? "success.light" : "grey.200",
        bgcolor: isEmpty ? "grey.50" : "background.paper",
      }}
    >
      <CardContent
        sx={{
          p: 1.25,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          "&:last-child": { pb: 1.25 },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0, mb: 1 }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: "grey.100",
              color: isComplete
                ? "success.main"
                : isEmpty
                  ? "text.disabled"
                  : "primary.main",
            }}
          >
            {icon}
          </Box>

          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {category.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {isEmpty
                ? "Nenhum documento"
                : `${docs.length} ${docs.length === 1 ? "arquivo" : "arquivos"}`}
            </Typography>
          </Stack>

          <Chip
            label={statusMeta.label}
            color={statusMeta.color}
            variant="outlined"
            sx={{
              flexShrink: 0,
              height: 28,
              fontWeight: 600,
              borderWidth: 1.5,
              "& .MuiChip-label": {
                px: 1.25,
                fontSize: "0.8125rem",
              },
            }}
          />
        </Stack>

        {isEmpty ? (
          <Typography variant="caption" color="text.secondary">
            Aguardando envio do paciente.
          </Typography>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              borderRadius: 1,
              border: 1,
              borderColor: "grey.200",
              bgcolor: "grey.50",
              p: 0.75,
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 0.875,
                alignContent: "start",
              }}
            >
              {docs.map((document, index) => (
                <DocumentTile
                  key={document.id}
                  doc={document}
                  index={index}
                  categoryLabel={category.label}
                  organizationId={organizationId}
                  canVerify={canVerify}
                  busyDocId={busyDocId}
                  approvingAll={approvingAll}
                inherited={Boolean(document.id && inheritedDocIds.includes(document.id))}
                onView={onView}
                onApprove={onApprove}
                onReject={onReject}
                onRevokeApproval={onRevokeApproval}
                onRevokeRejection={onRevokeRejection}
              />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});
